import { compile } from 'path-to-regexp';
import { debug } from '@isocel/front-common';

export enum RoutePathName {
    home = 'home',
    login = 'login',
    notFound = 'notFound',
    privacy = 'privacy',
    forgottenPassword = 'forgottenPassword',
    resetPassword = 'resetPassword',
    dashboard = 'dashboard',
    changePassword = 'changePassword',
    shipmentTrack = 'shipmentTrack',
    supportTrack = 'supportTrack',
    successShipment = 'successShipment',
    receptionView = 'receptionView',
    receptionDetails = 'receptionDetails',
    receptionComment = 'receptionComment',
    shipmentType = 'shipmentType',
    beforeLoading = 'beforeLoading',
    afterLoading = 'afterLoading',
    receptionStore = 'receptionStore',
    deliveryInfo = 'deliveryInfo',
    registration = 'registration',
    recipient = 'recipient',
    recipientMovement = 'recipientMovement',
    recipientTrack = 'recipientTrack',
    receptionInfo = 'receptionInfo',
    receptionMovement = 'receptionMovement',
    receptionFromSupplier = 'receptionFromSupplier',
    palettCreate = 'palettCreate',
    qrReading = 'qrReading',
    qrParcs = 'qrParcs',
    qrPackageTrack = 'qrPackageTrack',
    qrPackageUpdate = 'qrPackageUpdate',
    qrCamera = 'qrCamera',
    scaUsers = 'scaUsers',
    storeUsers = 'storeUsers',
    receptionsAValider = 'receptionsAValider',
    litigesAValider = 'litigesAValider',
    counters = 'counters',
    inventoryHistory = 'inventoryHistory',
    inventairesAValider = 'inventairesAValider',
    gestionMagasins = 'gestionMagasins',
    facturationManuelle = 'facturationManuelle',
    historiqueFacturation = 'historiqueFacturation',
}

export type Routes = {
    [r in RoutePathName]: string;
};

export const routes: Routes = {
    [RoutePathName.home]: '/',
    [RoutePathName.login]: '/login',
    [RoutePathName.notFound]: '/404',
    [RoutePathName.privacy]: '/confidentialite',
    [RoutePathName.forgottenPassword]: '/mot-de-passe-oublie',
    [RoutePathName.resetPassword]: '/reset-password',
    [RoutePathName.dashboard]: '/dashboard',
    [RoutePathName.shipmentTrack]: '/shipmentTracking',
    [RoutePathName.supportTrack]: '/supportTracking',
    [RoutePathName.successShipment]: '/successShipment',
    [RoutePathName.receptionView]: '/receptionView',
    [RoutePathName.receptionDetails]: '/receptionDetails',
    [RoutePathName.receptionComment]: '/receptionComment',
    [RoutePathName.shipmentType]: '/shipment-type',
    [RoutePathName.beforeLoading]: '/before-loading',
    [RoutePathName.afterLoading]: '/after-loading',
    [RoutePathName.deliveryInfo]: '/delivery-info',
    [RoutePathName.registration]: '/registration/:storeId?',
    [RoutePathName.recipient]: '/recipient',
    [RoutePathName.recipientMovement]: '/recipientMovement/:storeId?',
    [RoutePathName.recipientTrack]: '/recipientTrack/:storeId?',
    [RoutePathName.receptionInfo]: '/receptionInfo',
    [RoutePathName.receptionMovement]: '/receptionMovement/:id',
    [RoutePathName.receptionStore]: '/receptionStore',
    [RoutePathName.palettCreate]: '/palettCreate',
    [RoutePathName.receptionFromSupplier]: '/receptionFromSupplier',
    [RoutePathName.qrReading]: '/qrReading',
    [RoutePathName.qrParcs]: '/qrParcs',
    [RoutePathName.qrPackageTrack]: '/qrPackageTrack',
    [RoutePathName.qrPackageUpdate]: '/qrPackageUpdate',
    [RoutePathName.qrCamera]: '/qrCamera',
    [RoutePathName.scaUsers]: '/utilisateurs-sca',
    [RoutePathName.storeUsers]: '/magasin-store',
    [RoutePathName.changePassword]: '/change-password',
    [RoutePathName.receptionsAValider]: '/receptions-a-valider',
    [RoutePathName.litigesAValider]: '/litiges-a-valider',
    [RoutePathName.counters]: '/counters',
    [RoutePathName.inventoryHistory]: '/inventory-history',
    [RoutePathName.inventairesAValider]: '/inventaires-a-valider',
    [RoutePathName.gestionMagasins]: '/gestionMagasins',
    [RoutePathName.facturationManuelle]: '/facturation-manuelle',
    [RoutePathName.historiqueFacturation]: '/historique-facturation',
};

export interface RouteParams {
    [param: string]: string | number;
}

// returns raw react-router string path eg: /user/:id
export const getRawRoute = (path: RoutePathName) => {
    if (!routes[path]) {
        debug.error(`[getRawRoute] Route not found for path "${path}", returning /404.`);
        return '/404';
    } else {
        return routes[path];
    }
};

// returns real-world path eg: /user/1337
export const getRoute = (
    path: RoutePathName,
    params?: RouteParams,
    queryParams?: string | string[][] | Record<string, string> | URLSearchParams | undefined,
    anchor?: string
) => {
    let route = getRawRoute(path);
    const compiler = compile(route, { encode: encodeURIComponent });

    try {
        route = compiler(params);
    } catch (error) {
        debug.error('[getRoute] route compile error :', error);
    }

    if (queryParams && (queryParams instanceof URLSearchParams || Object.keys(queryParams).length)) {
        const urlQueryParams = new URLSearchParams(queryParams);
        route += `?${urlQueryParams.toString()}`;
    }

    if (anchor) {
        route = `${route}#${anchor}`;
    }

    return route;
};
